/**
 * Read-only rule queries shared by action handlers (on a mutable draft) and by the public
 * query API (on immutable state). Functions return the violated rule and reason, or null if allowed.
 */
import { angleSteps, isDiagonal, mustParse, rayBetween, step, toCellId, type Point } from './geometry.ts';
import { CELL_CAPACITY, indexMap, type MapIndex } from './map.ts';
import { block, type Block } from './reasons.ts';
import { isPowered } from './ruleset.ts';
import {
  DIRECTIONS,
  type CellId,
  type Direction,
  type FortState,
  type GameState,
  type ShipState,
  type Side,
} from './types.ts';

export type World = Pick<GameState, 'map' | 'ruleset' | 'ships' | 'forts' | 'turn'>;

export const otherSide = (side: Side): Side => (side === 'red' ? 'blue' : 'red');

export function findShip(w: World, id: string): ShipState | undefined {
  return w.ships.find((s) => s.id === id);
}

export function findFort(w: World, id: string): FortState | undefined {
  return w.forts.find((f) => f.id === id);
}

export function afloat(w: World, side?: Side): ShipState[] {
  return w.ships.filter((s) => !s.sunk && (side === undefined || s.side === side));
}

const pointOf = (cell: CellId) => mustParse(cell);

export function shipsAt(w: World, p: Point): ShipState[] {
  const id = toCellId(p);
  return w.ships.filter((s) => !s.sunk && s.cell === id);
}

/** The fort whose port contains `p`, if that fort is active (R-PORT-01). */
export function activePortFort(w: World, p: Point): FortState | null {
  const fortId = indexMap(w.map).portOf(p);
  if (fortId === null) return null;
  const fort = findFort(w, fortId);
  return fort && !fort.destroyed ? fort : null;
}

/** Ships counted towards the port limit (R-MAP-06): afloat, not docked. */
export function portLoad(w: World, fort: FortState, exclude?: string): number {
  return w.ships.filter(
    (s) => !s.sunk && s.dockedOnTurn === null && s.id !== exclude && fort.portCells.includes(s.cell),
  ).length;
}

export function isOnEnemyRoyal(w: World, ship: ShipState): boolean {
  const owner = indexMap(w.map).royalOf(pointOf(ship.cell));
  return owner !== null && owner !== ship.side;
}

/** Land, fort cells and the map edge (R-MOVE-05, R-MAP-09). */
function isHard(idx: MapIndex, p: Point): boolean {
  return !idx.inBounds(p) || idx.type(p) === 'land';
}

/** R-MOVE-11: diagonal step between two hard cells. */
function squeezed(w: World, from: Point, d: Direction): boolean {
  if (w.ruleset.diagonalSqueeze || !isDiagonal(d)) return false;
  const idx = indexMap(w.map);
  const to = step(from, d);
  return isHard(idx, { x: to.x, y: from.y }) && isHard(idx, { x: from.x, y: to.y });
}

/** Can `ship` enter (pass through or end in) cell `p` at all? */
export function entryBlock(w: World, ship: ShipState, p: Point): Block | null {
  const idx = indexMap(w.map);
  if (isHard(idx, p)) return block('R-MOVE-05', 'blockedByLand');
  if (idx.type(p) === 'reef') return block('R-MOVE-06', 'blockedByReef');
  if (idx.royalOf(p) === ship.side) return block('R-MOVE-10', 'ownRoyalPort');
  const fort = activePortFort(w, p);
  if (fort && fort.side !== ship.side) return block('R-MOVE-09', 'enemyPort');
  return null;
}

/** Can `ship` end its move in `p` (capacity, R-MOVE-08)? Assumes entryBlock passed. */
export function endBlock(w: World, ship: ShipState, p: Point): Block | null {
  const idx = indexMap(w.map);
  const others = shipsAt(w, p).filter((s) => s.id !== ship.id);
  if (others.length >= CELL_CAPACITY[idx.type(p)]) return block('R-MOVE-08', 'cellFull');
  const fort = activePortFort(w, p);
  if (fort && portLoad(w, fort, ship.id) >= w.ruleset.portShipLimit) return block('R-MOVE-08', 'portFull');
  return null;
}

/** Why `ship` cannot make its move this turn at all, or null. */
export function moveBlock(w: World, ship: ShipState): Block | null {
  if (ship.sunk) return block('R-FIRE-08', 'sunk');
  if (ship.side !== w.turn.side) return block('R-TURN-08', 'notYourUnit');
  if (ship.dockedOnTurn !== null) return block('R-SREP-02', 'inDock');
  if (ship.repairingFort !== null) return block('R-FREP-02', 'onFortRepair');
  if (ship.moved) return block('R-TURN-06', 'alreadyMoved');
  if (ship.firedInPort) return block('R-PORT-04', 'firedInPort');
  return null;
}

/** Maximum distance in direction `d` under the current spin (R-MOVE-02..04). */
export function maxDistance(w: World, ship: ShipState, d: Direction): number {
  const spin = w.turn.spin;
  if (!spin) return 0;
  const cls = w.ruleset.shipClasses[ship.cls];
  switch (spin.weather) {
    case 'fresh': {
      const n = Math.max(0, cls.freshSpeed - angleSteps(d, spin.direction));
      return isPowered(cls) && w.ruleset.oarsAndSteamUseBest ? Math.max(n, cls.calmSpeed) : n;
    }
    case 'calm':
      return cls.calmSpeed;
    case 'storm':
      return 0;
  }
}

/**
 * Validates a straight-line path of `distance` cells in direction `d` (R-MOVE-01, 05..11).
 * Returns the violated rule or null.
 */
export function pathBlock(w: World, ship: ShipState, d: Direction, distance: number): Block | null {
  let cur = pointOf(ship.cell);
  for (let i = 1; i <= distance; i++) {
    if (squeezed(w, cur, d)) return block('R-MOVE-11', 'diagonalSqueeze');
    const next = step(cur, d);
    const entry = entryBlock(w, ship, next);
    if (entry) return entry;
    if (i < distance && !w.ruleset.passThroughShips && shipsAt(w, next).length > 0) {
      return block('R-MOVE-07', 'shipInTheWay');
    }
    cur = next;
  }
  return endBlock(w, ship, cur);
}

/** Why `ship` cannot make a wind/calm move at all right now, or null. */
export function windPreBlock(w: World, ship: ShipState): Block | null {
  // Storm drift already consumed the group's move; report the more specific rule.
  if (ship.inGroup && w.turn.spin?.weather === 'storm') return block('R-STORM-01', 'stormDrifted');
  const base = moveBlock(w, ship);
  if (base) return base;
  const spin = w.turn.spin;
  if (w.turn.phase !== 'spun' || !spin) return block('R-TURN-04', 'windNotCast');
  if (!ship.inGroup) return block('R-TURN-05', 'notInSquadron');
  if (spin.weather === 'calm' && w.ruleset.shipClasses[ship.cls].calmSpeed === 0) {
    return block('R-MOVE-04', 'becalmed');
  }
  return null;
}

/** Validates a wind/calm move of `ship` to `to`. */
export function windMoveBlock(w: World, ship: ShipState, to: CellId): Block | null {
  const pre = windPreBlock(w, ship);
  if (pre) return pre;
  const ray = rayBetween(pointOf(ship.cell), pointOf(to));
  if (!ray || ray.direction === null) return block('R-MOVE-01', 'notInLine');
  if (ray.distance > maxDistance(w, ship, ray.direction)) {
    return block(w.turn.spin?.weather === 'calm' ? 'R-MOVE-04' : 'R-MOVE-02', 'tooFar');
  }
  return pathBlock(w, ship, ray.direction, ray.distance);
}

/** All cells `ship` may move to with a wind/calm move right now. */
export function windReachable(w: World, ship: ShipState): CellId[] {
  if (windPreBlock(w, ship)) return [];
  const out: CellId[] = [];
  const from = pointOf(ship.cell);
  for (const d of DIRECTIONS) {
    const max = maxDistance(w, ship, d);
    for (let n = 1; n <= max; n++) {
      const blocked = pathBlock(w, ship, d, n);
      if (blocked === null) out.push(toCellId(step(from, d, n)));
      // A full cell may still be passed through (R-MOVE-07); anything else ends the ray.
      else if (blocked.rule !== 'R-MOVE-08') break;
    }
  }
  return out;
}

/** R-PORT-03 */
export function portMoveBlock(w: World, ship: ShipState, to: CellId): Block | null {
  const base = moveBlock(w, ship);
  if (base) return base;
  if (ship.inGroup) return block('R-PORT-03', 'inSquadron');
  const fort = activePortFort(w, pointOf(ship.cell));
  if (!fort || fort.side !== ship.side) return block('R-PORT-03', 'notInOwnPort');
  if (to === ship.cell || !fort.portCells.includes(to)) return block('R-PORT-03', 'notInThisPort');
  return endBlock(w, ship, pointOf(to));
}

export function portReachable(w: World, ship: ShipState): CellId[] {
  const fort = activePortFort(w, pointOf(ship.cell));
  if (!fort) return [];
  return fort.portCells.filter((c) => portMoveBlock(w, ship, c) === null);
}

// ---------------------------------------------------------------------------
// Fire
// ---------------------------------------------------------------------------

export interface Shooter {
  readonly kind: 'ship' | 'fort';
  readonly id: string;
  readonly side: Side;
  readonly cell: CellId;
  readonly range: number;
  readonly shotsLeft: number;
}

export function shooterOf(w: World, id: string): Shooter | null {
  const ship = findShip(w, id);
  if (ship) {
    const cls = w.ruleset.shipClasses[ship.cls];
    return {
      kind: 'ship',
      id,
      side: ship.side,
      cell: ship.cell,
      range: cls.range,
      shotsLeft: ship.shotsLeft,
    };
  }
  const fort = findFort(w, id);
  if (fort) {
    const cls = w.ruleset.fortClasses[fort.cls];
    return {
      kind: 'fort',
      id,
      side: fort.side,
      cell: fort.cell,
      range: cls.range,
      shotsLeft: fort.shotsLeft,
    };
  }
  return null;
}

/** Can `id` fire `shots` shots now (ignoring the target)? */
export function shooterBlock(w: World, side: Side, id: string, shots: number): Block | null {
  const ship = findShip(w, id);
  if (ship) {
    if (ship.side !== side) return block('R-TURN-08', 'notYourUnit');
    if (ship.sunk) return block('R-FIRE-08', 'sunk');
    if (ship.dockedOnTurn !== null) return block('R-FIRE-06', 'cannotFireInDock');
    if (ship.repairingFort !== null) return block('R-FIRE-06', 'cannotFireOnRepair');
    if (isOnEnemyRoyal(w, ship)) return block('R-ROYAL-03', 'onEnemyRoyalPort');
    if (ship.shotsLeft < shots) return block('R-FIRE-04', 'noShotsLeft');
    return null;
  }
  const fort = findFort(w, id);
  if (!fort) return block('R-FIRE-01', 'unknownUnit');
  if (fort.destroyed) return block('R-FIRE-07', 'fortDestroyed');
  if (fort.side !== side) return block('R-TURN-08', 'notYourUnit');
  if (fort.shotsLeft < shots) return block('R-FIRE-04', 'noShotsLeft');
  return null;
}

/** Line of fire between two cells (R-FIRE-01..03). */
export function lineBlock(w: World, from: CellId, to: CellId, range: number): Block | null {
  const a = pointOf(from);
  const b = pointOf(to);
  const ray = rayBetween(a, b);
  if (!ray) return block('R-FIRE-01', 'notInLine');
  if (ray.distance > range) return block('R-FIRE-01', 'outOfRange');
  if (ray.direction !== null && w.ruleset.landBlocksFire) {
    const idx = indexMap(w.map);
    for (let i = 1; i < ray.distance; i++) {
      if (idx.type(step(a, ray.direction, i)) === 'land') return block('R-FIRE-02', 'lineOfFireBlocked');
    }
  }
  return null;
}

/** Can shooter `shooterId` (of `side`) target `targetId`? Ignores shots left. */
export function targetBlock(w: World, shooterId: string, targetId: string): Block | null {
  const shooter = shooterOf(w, shooterId);
  if (!shooter) return block('R-FIRE-01', 'unknownUnit');
  const ship = findShip(w, targetId);
  if (ship) {
    if (ship.side === shooter.side) return block('R-FIRE-01', 'friendlyTarget');
    if (ship.sunk) return block('R-FIRE-10', 'targetGone');
    return lineBlock(w, shooter.cell, ship.cell, shooter.range);
  }
  const fort = findFort(w, targetId);
  if (!fort) return block('R-FIRE-01', 'unknownUnit');
  if (shooter.kind === 'fort') return block('R-FIRE-01', 'fortCannotTargetFort');
  if (fort.destroyed) return block('R-FIRE-10', 'targetGone');
  if (fort.side === shooter.side) return block('R-FIRE-01', 'friendlyTarget');
  return lineBlock(w, shooter.cell, fort.cell, shooter.range);
}

/** Enemy units `shooterId` could hit by range and line of fire. */
export function targetsOf(w: World, shooterId: string): string[] {
  const ids = [...w.ships.map((s) => s.id), ...w.forts.map((f) => f.id)];
  return ids.filter((t) => targetBlock(w, shooterId, t) === null);
}
